import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cart");
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = useCallback(async (product_id, size, quantity = 1, color = null) => {
    await api.post("/cart/add", { product_id, size, quantity, color });
    await refresh();
  }, [refresh]);

  const removeItem = useCallback(async (product_id, size, color = null) => {
    await api.post("/cart/remove", { product_id, size, color, quantity: 1 });
    await refresh();
  }, [refresh]);

  const clear = useCallback(async () => {
    await api.post("/cart/clear");
    await refresh();
  }, [refresh]);

  const { count, total } = useMemo(() => ({
    count: items.reduce((s, it) => s + (it.quantity || 0), 0),
    total: items.reduce((s, it) => s + (it.product?.price || 0) * (it.quantity || 0), 0),
  }), [items]);

  const value = useMemo(
    () => ({ items, count, total, loading, addItem, removeItem, clear, refresh }),
    [items, count, total, loading, addItem, removeItem, clear, refresh],
  );

  return (
    <CartCtx.Provider value={value}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
